import * as Sentry from '@sentry/react'
import type { LoggedUser } from 'BrokerTsLib/src/dtos'
import { useSetReadAppInfoMessage } from 'BrokerTsLib/src/hooks/useAppInfoMessage'
import { createContext, useCallback, useContext, useEffect } from 'react'


import { useIsLogged, useLogout } from 'BrokerTsLib/src/hooks/useAccount'
import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { getPartnerByHost, getPartnerByKey } from '../appSettings/Partners'
import { usePartner } from './PartnerContext'

type AuthContextProps = {
  isLoading?: boolean,
  user: LoggedUser | null,
  setReadMessage: (msgId: number) => void
  logout: (returnUrl?: string, isAutomatic?: boolean) => void
  isLoggingOut: boolean
  reload: () => void
}

export const AuthContext = createContext<AuthContextProps | null>(null)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { setCurrentPartner } = usePartner()
  const { data: user, isLoading, error, refetch } = useIsLogged()
  const { logout, isLoggingOut } = useLogout()

  useEffect(() => {
    if (error && !isIn(error?.code, 'ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED'))
      logout()
  }, [error, logout])

  const { mutateAsync: setMessageRead } = useSetReadAppInfoMessage()

  const setReadMessage = (msgId: number) => {
    setMessageRead({ id: msgId, read: true })
  }

  useEffect(() => {
    setCurrentPartner(user?.clientKey
      ? getPartnerByKey(user.clientKey as any)
      : getPartnerByHost())
  }, [user])

  useEffect(() => {
    if (user)
      Sentry.setUser({
        id: user?.id,
        username: user?.userName
      })
    else
      Sentry.setUser(null)
  }, [user])

  const reload = useCallback(() => {
    refetch()
  }, [refetch])

  return (

    <AuthContext.Provider value={{
      isLoading,
      user: user ?? null,
      setReadMessage,
      logout,
      isLoggingOut,
      reload
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context || context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider')
  }

  return context
}